import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ButtonWithLoading, ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Box, Button, Checkbox, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Arbor } from "../../models/Arbor";
import { Donut } from "../../models/Donut";
import { Roll } from "../../models/Roll";

export const MultipleDeleteSelectModal = ({
	open,
	onClose,
	deleteFunction,
	selectedRows,
	entity,
}: {
	open: boolean;
	onClose: (deleted: boolean) => void;
	deleteFunction: (partId: string) => Promise<void>;
	selectedRows: (Arbor | Donut | Roll)[];
	entity?: { queryKey?: string; name?: string };
}) => {
	const queryClient = useQueryClient();

	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());

	const handleCheckboxChange = (partId: number, checked: boolean) => {
		setCheckedItems((prev) => {
			const newSet = new Set(prev);
			if (checked) {
				newSet.add(partId);
			} else {
				newSet.delete(partId);
			}
			return newSet;
		});
	};

	const isRoll = (row: Arbor | Donut | Roll): row is Roll =>
		"RollPosition" in row;
	const isArbor = (row: Arbor | Donut | Roll): row is Arbor =>
		"StandsId" in row;
	const isDonut = (row: Arbor | Donut | Roll): row is Donut =>
		"DonutStatusCode" in row;

	const getRowLabel = (row: Arbor | Donut | Roll): string => {
		const partName = `'${row.PartName}'`;

		if (row.IsInActiveCarrier) {
			return `${partName} - Can't delete - Installed in Carrier '${row.CarrierName}' (${row.CarrierStatusName})`;
		}

		const rollsetInfo = `Rollset '${row.RollsetSectionName}-${row.RollsetStandName}-${row.RollsetLetter}'`;

		if (isRoll(row)) {
			if (row.IsInActiveRollset) {
				return `${partName} will deactivate ${rollsetInfo}`;
			}
		}

		if (isArbor(row)) {
			const universalRollInfo = `Universal Roll '${row.UniversalRollName}'`;
			if (row.IsInActiveRollset) {
				return `${partName} will deactivate ${rollsetInfo} and ${universalRollInfo}`;
			}
			if (row.UniversalRollName) {
				return `${partName} will deactivate ${universalRollInfo}`;
			}
		}

		if (isDonut(row)) {
			const universalRollInfo = `Universal Roll '${row.UniversalRollName}'`;
			if (row.IsInActiveRollset) {
				return `${partName} will deactivate ${rollsetInfo} and update ${universalRollInfo}`;
			}
			if (row.UniversalRollName) {
				return `${partName} will update ${universalRollInfo}`;
			}
		}

		return row.PartName;
	};

	const deleteMutation = useMutation(deleteFunction, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [`${entity?.queryKey ? entity?.queryKey : entity?.name}s`],
			});
			setMessage(
				`The ${entity?.name?.toLowerCase()}s were deleted successfully`,
			);
			onClose(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const deletableCount = selectedRows.filter(
		(row) => !row.IsInActiveCarrier,
	).length;
	const checkedCount = checkedItems.size;

	useEffect(() => {
		if (open) {
			const initialChecked = new Set<number>();
			selectedRows.forEach((row) => {
				if (!row.IsInActiveCarrier) {
					initialChecked.add(row.PartId!);
				}
			});
			setCheckedItems(initialChecked);
		}
	}, [open, selectedRows]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={() => onClose(false)}
				title={`Delete multiple ${entity?.name}s`}
			>
				<MesfModal.Content dividers>
					<Typography sx={{ mb: 2 }}>
						Are you sure you want to delete the selected{" "}
						{entity?.name?.toLowerCase()}s?
					</Typography>
					{selectedRows.length > 0 && (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								gap: 1,
								overflowY: "auto",
								maxHeight: 300,
							}}
						>
							{selectedRows.map((row) => {
								const isDisabled = row.IsInActiveCarrier;
								return (
									<Box
										key={row.PartId}
										sx={{
											display: "flex",
											alignItems: "center",
											borderRadius: 2,
											px: 1,
											cursor: "pointer",
										}}
										onClick={
											isDisabled
												? () => {}
												: () => {
														handleCheckboxChange(
															row.PartId!,
															!checkedItems.has(row.PartId!),
														);
													}
										}
									>
										<Checkbox
											checked={checkedItems.has(row.PartId!)}
											onChange={(e) =>
												handleCheckboxChange(row.PartId!, e.target.checked)
											}
											color="primary"
											disabled={isDisabled}
										/>
										<Typography
											fontWeight={isDisabled ? 500 : 400}
											color={isDisabled ? "textSecondary" : "text.primary"}
											sx={{ userSelect: "none" }}
										>
											{getRowLabel(row)}
										</Typography>
									</Box>
								);
							})}
						</Box>
					)}
					{deletableCount < selectedRows.length && (
						<Typography
							variant="caption"
							color="warning.main"
							sx={{ mt: 1, display: "block" }}
						>
							Items in active carriers cannot be deleted.
						</Typography>
					)}
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={() => onClose(false)} color="inherit">
						Cancel
					</Button>
					<ButtonWithLoading
						isLoading={deleteMutation.isLoading}
						disabled={checkedCount === 0}
						onClick={() => {
							const partIdsString = Array.from(checkedItems).join(",");
							deleteMutation.mutate(partIdsString);
						}}
						color="error"
						variant="contained"
						data-test-id="confirm-delete-selected-button"
					>
						Delete Selected ({checkedCount})
					</ButtonWithLoading>
				</MesfModal.Actions>
			</MesfModal>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title={`Error Deleting ${entity?.name}s`}
			/>
		</>
	);
};
